<template>
  <div class="page-container">
    <!-- 工具条 -->
    <div class="toolbar">
      <div class="left">
        <el-input v-model="search.keyword" placeholder="按名称/voice_id 搜索" clearable style="width: 280px"
          @keyup.enter="doSearch">
          <template #prefix><el-icon>
              <Search />
            </el-icon></template>
        </el-input>
        <el-button type="primary" @click="doSearch" style="margin-left: 10px">
          <el-icon>
            <Search />
          </el-icon>搜索
        </el-button>

        <!-- 批量删除 -->
        <el-popconfirm v-if="selectedIds.length" :title="`确认删除选中的 ${selectedIds.length} 条记录吗？`"
          @confirm="doBatchDelete">
          <template #reference>
            <el-button type="danger" style="margin-left: 10px" :disabled="!selectedIds.length">
              批量删除<span v-if="selectedIds.length">（{{ selectedIds.length }}）</span>
            </el-button>
          </template>
        </el-popconfirm>
      </div>

      <div class="right">
        <el-button type="success" @click="openAdd">AI克隆</el-button>
      </div>
    </div>

    <!-- 列表 -->
    <el-table :data="list" border stripe v-loading="loading" style="width: 100%"
      @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="48" />
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="voice_name" label="名称" width="220" show-overflow-tooltip />
      <!-- 新增：克隆描述 -->
      <el-table-column prop="voice_describe" label="克隆描述" min-width="220" show-overflow-tooltip />

      <el-table-column prop="voice_id" label="音色ID" min-width="260" show-overflow-tooltip />
      <!-- 替换：素材地址 -> 可播放的音频列表 -->
      <el-table-column label="音频素材" min-width="320">
        <template #default="s">
          <div class="audio-list">
            <!-- 前两条直链内联显示 -->
            <div v-for="(u, idx) in splitByAnyComma(s.row.human_voice_url).slice(0, 2)" :key="u + idx"
              class="audio-item">
              <audio :src="u" controls preload="metadata" />
              <el-button link type="primary" @click="copyText(u)">复制</el-button>
            </div>

            <!-- 若超过2条，用气泡展示“更多” -->
            <el-popover v-if="splitByAnyComma(s.row.human_voice_url).length > 2" placement="bottom" width="380"
              trigger="click">
              <template #reference>
                <el-button type="primary" link>更多 {{ splitByAnyComma(s.row.human_voice_url).length - 2 }}</el-button>
              </template>
              <div class="audio-popover">
                <div v-for="(u, idx) in splitByAnyComma(s.row.human_voice_url).slice(2)" :key="u + 'more' + idx"
                  class="audio-item">
                  <audio :src="u" controls preload="metadata" />
                  <el-button link type="primary" @click="copyText(u)">复制</el-button>
                </div>
              </div>
            </el-popover>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="性别" width="90">
        <template #default="s">
          <el-tag size="small">{{ s.row.voice_sex === 2 ? '女' : '男' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="s">
          <el-tag :type="s.row.status === 1 ? 'success' : 'danger'">{{ s.row.status === 1 ? '正常' : '失败' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="克隆时间" width="180" />
      <el-table-column label="操作" width="120" fixed="right">
        <template #default="s">
          <el-button type="danger" size="small" @click="delOne(s.row.id)">
            <el-icon>
              <Delete />
            </el-icon>删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pager">
      <el-pagination v-model:current-page="pager.page" v-model:page-size="pager.pageSize" :total="pager.total"
        :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper" @size-change="fetchList"
        @current-change="fetchList" />
    </div>

    <!-- 新增弹窗 -->
    <el-dialog v-model="dialogVisible" title="AI声音克隆" width="780px" :close-on-click-modal="false">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
        <!-- 自定义名称（支持中/英逗号；追加分隔按钮） -->
        <el-form-item label="自定义名称" prop="voice_name">
          <el-input v-model="form.voice_name" placeholder="支持多个，用中文/英文逗号分隔">
            <template #append>
              <el-button @click="insertSep('voice_name')" text>插入分隔符</el-button>
            </template>
          </el-input>
        </el-form-item>

        <!-- 克隆描述（必填，支持多条） -->
        <el-form-item label="克隆描述" prop="voice_describe">
          <el-input v-model="form.voice_describe" placeholder="必填；支持多个，用中文/英文逗号分隔。">
            <template #append>
              <el-button @click="insertSep('voice_describe')" text>插入分隔符</el-button>
            </template>
          </el-input>
          <div class="hint">示例：温暖、自然；或：讲解型、磁性；长度不限，建议简洁准确</div>
        </el-form-item>

        <!-- 音频素材（多选上传） -->
        <el-form-item label="音频素材" prop="human_voice_url">
          <el-input v-model="form.human_voice_url" placeholder="粘贴音频直链，支持多个，用英文逗号 , 分隔" />
          <div class="inline-actions">
            <el-upload class="drop-audio" drag multiple :auto-upload="false" :show-file-list="false"
              accept=".mp3,.wav,.m4a,.aac" :on-change="onPickLocalAudio">
              <div class="drop-inner">
                <el-icon>
                  <Upload />
                </el-icon>
                <div class="drop-title">将音频拖到此处</div>
                <div class="drop-sub">或 <span class="link">点击选择</span>（可多选）</div>
                <div class="drop-meta">建议原始时长 ≥ 10 秒</div>
              </div>
            </el-upload>

            <div v-if="uploadedUrls.length" class="url-chips">
              <el-tag v-for="(u, i) in uploadedUrls" :key="u" closable @close="removeUrl(i)" type="info" effect="plain">
                {{ u }}
              </el-tag>
            </div>

            <!-- 上传队列（可滚动） -->
            <div v-if="queue.length" class="queue">
              <div class="queue-title">上传进度</div>
              <div v-for="q in queue" :key="q.id" class="queue-item">
                <span class="q-name" :title="q.name">{{ q.name }}</span>
                <el-progress :percentage="q.percent" :status="q.status" />
              </div>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="性别">
          <el-radio-group v-model="form.voice_sex">
            <el-radio :label="1">男</el-radio>
            <el-radio :label="2">女</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item>
          <!-- 复选框在未阅读前不可勾选 -->
          <el-checkbox v-model="agree" :disabled="!agreeEnabled">
            我已阅读并同意
            <el-link type="primary" :underline="false" @click.prevent="openAgreement">《克隆使用协议》</el-link>
          </el-checkbox>
          <span v-if="agreeCountdown > 0" style="margin-left:8px;color:#9ca3af;">（请至少阅读 {{ agreeCountdown }} 秒）</span>
        </el-form-item>

        <!-- 动态价格提示：单价 & 预计总消耗 -->
        <el-alert :title="priceTip" type="info" :closable="false" show-icon />
      </el-form>

      <!-- footer 必须是 el-dialog 的直接子节点 -->
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" :disabled="!agreeReady || urlList.length === 0" @click="submit">
          {{ submitBtnText }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 使用协议弹窗 -->
    <el-dialog v-model="agreementDialogVisible" :title="agreementTitle || '克隆使用协议'" width="720px"
      :close-on-click-modal="false" @closed="stopAgreeCountdown">
      <div v-loading="agreementLoading" style="min-height: 200px;">
        <div v-if="agreementUpdatedAt" style="color:#9ca3af;font-size:12px;margin-bottom:8px;">
          最近更新：{{ agreementUpdatedAt }}
        </div>
        <div class="agreement-content" v-html="agreementContent"></div>
      </div>
      <template #footer>
        <el-button @click="agreementDialogVisible = false">关闭</el-button>
        <el-button type="primary" :disabled="agreeCountdown > 0" @click="acceptAgreement">
          已阅读（{{ agreeCountdown > 0 ? agreeCountdown + 's' : '可确认' }}）
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config.js'
import { Search, Delete, Upload } from '@element-plus/icons-vue'
import { getUploadSignature, uploadToOSS, beforeUpload, getVideoDuration } from '@/utils/uploadUtils'

export default {
  name: 'VoiceClone',
  components: { Search, Delete, Upload },
  data() {
    return {
      loading: false,
      list: [],
      pager: { page: 1, pageSize: 10, total: 0 },
      search: { keyword: '' },

      // 批量选择
      selectedIds: [],

      dialogVisible: false,
      submitting: false,

      // 协议同意机制
      agree: false,
      agreeEnabled: false,
      agreeCountdown: 0,
      agreeTimer: null,

      // 协议内容
      agreementDialogVisible: false,
      agreementLoading: false,
      agreementTitle: '',
      agreementUpdatedAt: '',
      agreementContent: '',

      // 上传
      processedUids: new Set(),
      queue: [],
      uploadedUrls: [],

      // 表单
      form: {
        voice_name: '',
        voice_describe: '',   // 新增字段
        human_voice_url: '',
        voice_sex: 1
      },
      rules: {
        voice_name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        voice_describe: [{ required: true, message: '请填写克隆描述（必填）', trigger: 'blur' }],
        human_voice_url: [{ required: true, message: '请填写音频直链', trigger: 'blur' }]
      },

      // 价格
      unitPrice: 0
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    urlList() {
      return this.splitByAnyComma(this.form.human_voice_url)
    },
    nameList() {
      return this.splitByAnyComma(this.form.voice_name)
    },
    descList() {
      return this.splitByAnyComma(this.form.voice_describe)
    },
    totalCost() {
      return (this.unitPrice || 0) * (this.urlList.length || 0)
    },
    priceTip() {
      return `AI克隆（${this.unitPrice} 积分/次，以后台配置为准），本次预计消耗：${this.unitPrice} × ${this.urlList.length || 0} = ${this.totalCost}`
    },
    submitBtnText() {
      return `AI克隆（预计 ${this.totalCost} 积分）`
    },
    agreeReady() {
      return this.agree && this.agreeCountdown === 0
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, {
          action: this.search.keyword ? 'search' : 'list',
          store_id: this.userInfo.id,
          keyword: this.search.keyword,
          page: this.pager.page,
          page_size: this.pager.pageSize
        })
        this.list = data.data || []
        this.pager.total = data.pagination ? data.pagination.total : (this.list?.length || 0)
        if (!this.list.length && this.pager.page > 1) {
          this.pager.page = this.pager.page - 1
          await this.fetchList()
        }
        this.selectedIds = []
      } catch (e) {
        this.$message.error('获取列表失败')
      } finally {
        this.loading = false
      }
    },

    copyText(text) {
      if (!text) return
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success('已复制链接'))
          .catch(() => this.$message.error('复制失败'))
      } else {
        // 兼容回退
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        try {
          document.execCommand('copy')
          this.$message.success('已复制链接')
        } catch (e) {
          this.$message.error('复制失败')
        } finally {
          document.body.removeChild(ta)
        }
      }
    },

    doSearch() {
      this.pager.page = 1
      this.fetchList()
    },

    handleSelectionChange(rows) {
      this.selectedIds = rows.map(r => r.id)
    },
    async doBatchDelete() {
      if (!this.selectedIds.length) return
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, {
          action: 'batch_delete', store_id: this.userInfo.id, ids: this.selectedIds
        })
        if (data.success) {
          this.$message.success('批量删除成功')
          await this.fetchList()
        } else {
          this.$message.error(data.error || '批量删除失败')
        }
      } catch (e) {
        this.$message.error('批量删除失败')
      } finally {
        this.loading = false
      }
    },

    async openAdd() {
      this.dialogVisible = true
      // 协议状态复位
      this.agree = false
      this.agreeEnabled = false
      this.stopAgreeCountdown()

      // 表单/上传复位
      this.form = { voice_name: '', voice_describe: '', human_voice_url: '', voice_sex: 1 }
      this.uploadedUrls = []
      this.queue = []
      this.processedUids.clear()
      this.$nextTick(() => this.$refs.formRef?.clearValidate?.())

      // 单价
      try {
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_digital_human_config.php`)
        const v = Number(parseFloat(data?.data?.digital_human_voice_clone_num || 0).toFixed(2))
        this.unitPrice = isNaN(v) ? 0 : v
      } catch {
        this.unitPrice = 0
      }
    },

    insertSep(field) {
      const val = (this.form[field] ?? '').toString()
      const endsWithComma = /[，,]\s*$/.test(val)
      this.form[field] = endsWithComma ? `${val} ` : `${val}${val ? ', ' : ''}`
    },
    splitByAnyComma(str) {
      return (str || '')
        .split(/[，,]/g)
        .map(s => s.trim())
        .filter(Boolean)
    },

    async onPickLocalAudio(uploadFile) {
      try {
        const uid = uploadFile?.uid
        const file = uploadFile?.raw || uploadFile
        if (!file) return
        if (uid && this.processedUids.has(uid)) return
        if (uid) this.processedUids.add(uid)

        const q = { id: uid || `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`, name: file.name || '未命名音频', percent: 0, status: undefined }
        this.queue.push(q)

        const ok = await beforeUpload(file, { type: 'audio' })
        if (!ok) { q.status = 'exception'; return }
        q.percent = 10

        try {
          const secs = await getVideoDuration(file)
          if (!isNaN(secs) && secs < 5) {
            this.$message.warning(`${q.name} 时长较短（≈${secs.toFixed(1)}s），建议 ≥ 10 秒以保证质量`)
          }
        } catch (_) {
          // 读取音频时长失败不影响流程；写一条 debug 输出即可规避 no-empty
          console.debug('getVideoDuration failed', _)
        }

        q.percent = 45

        const sign = await getUploadSignature('audio')
        const res = await uploadToOSS(file, sign)
        q.percent = 90

        if (res?.success && res.url) {
          this.uploadedUrls.push(res.url)
          const manual = (this.form.human_voice_url || '').split(',').map(s => s.trim()).filter(Boolean)
          const total = Array.from(new Set([...manual, ...this.uploadedUrls]))
          this.form.human_voice_url = total.join(',')
          q.percent = 100
          q.status = 'success'
          this.$message.success(`上传成功：${q.name}`)
        } else {
          q.status = 'exception'
          this.$message.error(`上传失败：${q.name}`)
        }
      } catch (err) {
        console.error(err)
        this.$message.error(err?.message || '上传失败')
      }
    },
    removeUrl(idx) {
      this.uploadedUrls.splice(idx, 1)
      const manual = this.splitByAnyComma(this.form.human_voice_url)
      const remained = manual.filter(u => this.uploadedUrls.includes(u))
      this.form.human_voice_url = remained.join(',')
    },

    async openAgreement() {
      try {
        this.agreementDialogVisible = true
        this.agreementLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_clone_agreement.php`)
        if (data?.code === 200 && data?.data) {
          this.agreementTitle = data.data.title || '克隆使用协议'
          this.agreementUpdatedAt = data.data.updated_at || ''
          this.agreementContent = data.data.content || ''
        } else {
          this.agreementTitle = '克隆使用协议'
          this.agreementUpdatedAt = ''
          this.agreementContent = ''
        }
      } catch (e) {
        this.$message.error('读取使用协议失败')
        this.agreementContent = ''
      } finally {
        this.agreementLoading = false
        this.startAgreeCountdown(3)
      }
    },
    startAgreeCountdown(sec) {
      this.stopAgreeCountdown()
      this.agreeCountdown = sec
      this.agreeTimer = setInterval(() => {
        if (this.agreeCountdown > 0) this.agreeCountdown--
        if (this.agreeCountdown <= 0) this.stopAgreeCountdown()
      }, 1000)
    },
    stopAgreeCountdown() {
      if (this.agreeTimer) clearInterval(this.agreeTimer)
      this.agreeTimer = null
    },
    acceptAgreement() {
      if (this.agreeCountdown > 0) return
      this.agreementDialogVisible = false
      this.agreeEnabled = true
      this.agree = true
    },

    async submit() {
      this.$refs.formRef.validate(async (ok) => {
        if (!ok) return
        if (!this.agreeReady) { this.$message.warning('请先阅读并同意克隆使用协议'); return }
        if (this.urlList.length === 0) { this.$message.warning('请至少提供一个音频直链'); return }

        this.submitting = true
        try {
          // 这里直接把数组或字符串给后端，后端会统一解析
          const payload = {
            action: 'add',
            store_id: this.userInfo.id,
            voice_name: this.nameList.length > 1 ? this.nameList : (this.nameList[0] || ''),
            voice_describe: this.descList.length > 1 ? this.descList : (this.descList[0] || ''), // 新增
            human_voice_url: this.urlList.length > 1 ? this.urlList : (this.urlList[0] || ''),
            voice_sex: this.form.voice_sex
          }
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, payload)
          if (data.success) {
            this.$message.success('已提交克隆任务')
            this.dialogVisible = false
            this.fetchList()
          } else {
            this.$message.error(data.error || '提交失败')
          }
        } catch (e) {
          this.$message.error('提交失败')
        } finally {
          this.submitting = false
        }
      })
    },

    async delOne(id) {
      try {
        await this.$confirm('确定删除该记录吗？', '提示', { type: 'warning' })
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, { action: 'delete', id, store_id: this.userInfo.id })
        if (data.success) {
          this.$message.success('已删除')
          this.fetchList()
        } else {
          this.$message.error(data.error || '删除失败')
        }
      } catch (e) {
        console.info('删除操作已取消或出错：', e)
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 16px 20px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.pager {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.inline-actions {
  margin-top: 8px;
}

.hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 6px;
}

/* 上传卡片 */
.drop-audio {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #fff;
  transition: border-color .2s, box-shadow .2s;
  margin-top: 8px;
}

.drop-audio:hover {
  border-color: #93c5fd;
  box-shadow: 0 8px 24px rgba(59, 130, 246, .08);
}

.drop-inner {
  padding: 18px 12px;
  text-align: center;
  color: #6b7280;
}

.drop-title {
  font-weight: 600;
  color: #1f2937;
}

.drop-sub .link {
  color: #2563eb;
  cursor: pointer;
}

/* URL 标签 */
.url-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

/* 上传队列（滚动） */
.queue {
  margin-top: 10px;
  max-height: 220px;
  overflow-y: auto;
}

.queue-title {
  font-weight: 600;
  margin-bottom: 6px;
  color: #374151;
}

.queue-item {
  margin-bottom: 10px;
}

.q-name {
  display: block;
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}

/* 协议内容 */
.agreement-content {
  max-height: 60vh;
  overflow-y: auto;
  white-space: pre-wrap;
  line-height: 1.7;
}

.audio-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.audio-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.audio-item audio {
  width: 240px;
  max-width: 100%;
}

.audio-popover {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
